import React, { useEffect, useMemo, useState, useCallback } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import MenuEditsModal from "./components/Modals/MenuEditsModal";
import MenuHideModal from "./components/Modals/MenuHideModal";
import { useAppServices } from "../../../../hook/services";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import AddNewMenuModal from "./components/Modals/AddNewMenuModal";

const EditMenus = () => {
  const { companyId, type, customizationId } = useParams();
  const MenuType = type;
  const AppService = useAppServices();
  const handler =
    type === "agency" || type === "location"
      ? AppService.sidenav_customizer_main_level
      : type === "group"
        ? AppService.sidenav_customizer_custom_level
        : AppService.sidenav_customizer_saas_level;

  let query =
    type === "location" || type === "agency"
      ? `companyId=${companyId}&type=${type}`
      : `_id=${customizationId}`;

  const [loading, setLoading] = useState(true);
  const [customMenuLoading, setCustomMenuLoading] = useState(true);
  const [data, setData] = useState({});
  const [menuGroups, setMenuGroups] = useState([]);
  const [customMenuLinks, setCustomMenuLinks] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  const mergeMenus = (arr1, arr2) => {
    const map = new Map(arr2.map((item) => [item.selector, item]));

    const usedSelectors = new Set();

    let result = arr1
      .map((item) => {
        const match = map.get(item.selector);

        if (item.isCustom && !match) {
          return null;
        }

        if (match) {
          usedSelectors.add(item.selector);

          return {
            ...item,
            link: match.link ?? item.link,
          };
        }

        return item;
      })
      .filter(Boolean);

    arr2.forEach((item) => {
      if (!usedSelectors.has(item.selector)) {
        result.push({
          ...item,
          isCustom: true,
          children: item.children || [],
        });
      }
    });

    return result.map((item, i) => ({
      ...item,
      index: i + 1,
    }));
  };

  const updateMenusInDB = async (result) => {
    const payload = {
      companyId,
      menus: result,
      type,
      ...(data?._id && { _id: data._id }),
    };
    try {
      const { response } = await handler[data?._id ? "update" : "create"]({
        payload,
      });
    } catch (error) {}
  };

  useEffect(() => {
    if (customMenuLoading) return;
    const result = mergeMenus(menuGroups, customMenuLinks);
    if (result.length < 15) return;
    setMenuGroups(result);
    updateMenusInDB(result);
  }, [customMenuLinks]);

  const getCustomMenuLinks = useCallback(async () => {
    try {
      const { response } = await AppService.webhook.get_ghl_custom_menu_links({
        query: `companyId=${companyId}`,
      });
      setCustomMenuLinks(response.data || []);
    } catch (error) {
    } finally {
      setCustomMenuLoading(false);
    }
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { response } = await handler.filter({
        query: query,
      });

      if (response?.data) {
        setData(response.data);
        setMenuGroups(response.data?.menus || []);
      }
    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      getCustomMenuLinks();
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, []);

  const sortedVisibleGroups = useMemo(() => {
    if (!menuGroups) return [];

    return menuGroups
      .sort((a, b) => {
        const aIndex = a?.index ?? Infinity;
        const bIndex = b?.index ?? Infinity;

        return aIndex - bIndex;
      })
      .filter((group) => {
        const lowerSearch = searchTerm.toLowerCase();
        return (
          group.label.toLowerCase().includes(lowerSearch) ||
          group.children?.some((c) =>
            c.label.toLowerCase().includes(lowerSearch),
          )
        );
      })
      .map((group) => ({
        ...group,
        children: searchTerm
          ? group.children.filter((c) =>
              c.label.toLowerCase().includes(searchTerm.toLowerCase()),
            )
          : group.children,
      }));
  }, [menuGroups, searchTerm]);

  const handleDragEnd = async ({ source, destination, type }) => {
    if (!destination) return;

    const updatedGroups = [...menuGroups];
    const getGroupById = (id) =>
      updatedGroups.find((g) => String(g.value) === String(id));

    const getSiblingsContext = (list, destinationIndex) => {
      const before = destinationIndex > 0 ? list[destinationIndex - 1] : null;
      const next =
        destinationIndex < list.length - 1 ? list[destinationIndex + 1] : null;
      return { before, next };
    };

    if (type === "main") {
      const [moved] = updatedGroups.splice(source.index, 1);
      updatedGroups.splice(destination.index, 0, moved);

      const { before, next } = getSiblingsContext(
        updatedGroups,
        destination.index,
      );
      moved.index = destination.index;
      moved.repositionMenu = true;
      moved.menuPlacement = next ? "before" : "after";
      moved.menuTarget = next
        ? { value: next.value, selector: next.selector }
        : before
          ? { value: before.value, selector: before.selector }
          : null;
    }

    if (type === "nested") {
      const parent = getGroupById(source.droppableId);
      if (!parent) return;

      const newChildren = [...parent.children];
      const [moved] = newChildren.splice(source.index, 1);
      newChildren.splice(destination.index, 0, moved);

      const { before, next } = getSiblingsContext(
        newChildren,
        destination.index,
      );

      moved.index = destination.index;
      moved.repositionMenu = true;
      moved.menuPlacement = next ? "before" : "after";
      moved.menuTarget = next
        ? { value: next.value, selector: next.selector }
        : before
          ? { value: before.value, selector: before.selector }
          : null;

      parent.children = newChildren.map((item, idx) => ({
        ...item,
        index: idx,
      }));
    }

    const reindex = (items) =>
      items.map((item, idx) => ({
        ...item,
        index: idx,
        children: item.children ? reindex(item.children) : [],
      }));

    const reindexedGroups = reindex(updatedGroups);

    setMenuGroups(reindexedGroups);
    setData((prev) => ({ ...prev, menus: reindexedGroups }));

    try {
      const payload = {
        companyId,
        type: MenuType,
        menus: reindexedGroups,
        ...(data._id && { _id: data._id }),
      };

      await handler[data._id ? "update" : "create"]({ payload });
      toast.success("Menu order updated successfully");
    } catch (err) {
      console.error("Update error:", err);
      toast.error("Failed to update menu order");
    }
  };

  if (loading || customMenuLoading)
    return (
      <div className="edit-menus-loading d-flex flex-column align-items-center justify-content-center py-5 text-secondary">
        <div className="spinner-border spinner-border-sm mb-2" role="status" aria-hidden="true" />
        <span style={{ fontSize: "0.8125rem" }}>Loading menus…</span>
        <EditMenusStyles />
      </div>
    );

  const hasResults = sortedVisibleGroups.length > 0;

  return (
    <div className="edit-menus">
      {/* Toolbar */}
      <div className="edit-menus-toolbar d-flex flex-wrap align-items-center gap-2 mb-4">
        <div className="edit-menus-search position-relative flex-grow-1">
          <span className="edit-menus-search-icon d-flex align-items-center justify-content-center">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="7" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
          </span>
          <input
            type="search"
            className="edit-menus-search-input"
            placeholder="Search menus"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button
              type="button"
              className="edit-menus-search-clear"
              onClick={() => setSearchTerm("")}
              aria-label="Clear search"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
        </div>
        <div className="ms-auto">
          <AddNewMenuModal data={data} handleRefresh={fetchData} />
        </div>
      </div>

      {!hasResults ? (
        <div className="edit-menus-empty d-flex flex-column align-items-center justify-content-center text-center py-5">
          <div className="edit-menus-empty-icon d-flex align-items-center justify-content-center mb-3">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="7" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
          </div>
          <div className="fw-semibold text-dark mb-1" style={{ fontSize: "0.9375rem" }}>
            No menus found
          </div>
          <div className="text-secondary" style={{ fontSize: "0.8125rem" }}>
            {searchTerm ? `No menus match “${searchTerm}”.` : "There are no menus yet."}
          </div>
        </div>
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="main-list" type="main">
            {(providedMain) => (
              <div
                ref={providedMain.innerRef}
                {...providedMain.droppableProps}
                className="d-grid gap-3"
              >
                {sortedVisibleGroups.map((group, idx) => (
                  <Draggable
                    key={group.value}
                    draggableId={group.value}
                    index={idx}
                  >
                    {(providedGrp, snapshot) => (
                      <div
                        ref={providedGrp.innerRef}
                        {...providedGrp.draggableProps}
                        className={`edit-menus-card ${snapshot.isDragging ? "is-dragging" : ""}`}
                      >
                        <div className="edit-menus-card-header d-flex justify-content-between align-items-center">
                          <div
                            {...providedGrp.dragHandleProps}
                            className="d-flex align-items-center gap-2 flex-grow-1 edit-menus-drag-area"
                          >
                            <span className="edit-menus-grip" aria-hidden="true">
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="9" cy="6" r="1.2" />
                                <circle cx="9" cy="12" r="1.2" />
                                <circle cx="9" cy="18" r="1.2" />
                                <circle cx="15" cy="6" r="1.2" />
                                <circle cx="15" cy="12" r="1.2" />
                                <circle cx="15" cy="18" r="1.2" />
                              </svg>
                            </span>
                            <span className="edit-menus-icon d-inline-flex align-items-center justify-content-center">
                              {group?.isCustom ? (
                                <i className={`${group.icon}`} />
                              ) : (
                                <i
                                  class="fa"
                                  dangerouslySetInnerHTML={{
                                    __html: group?.icon,
                                  }}
                                ></i>
                              )}
                            </span>
                            <span className="edit-menus-label">
                              {group?.newLabel || group.label}
                            </span>
                            {group?.isCustom && (
                              <span className="edit-menus-badge">Custom</span>
                            )}
                            {group?.type === "hide" && (
                              <span className="edit-menus-badge edit-menus-badge--muted">Hidden</span>
                            )}
                          </div>
                          <div className="d-flex align-items-center gap-1">
                            <MenuHideModal
                              selectedItem={group}
                              data={data}
                              handleRefresh={fetchData}
                            />
                            <MenuEditsModal
                              selectedItem={group}
                              data={data}
                              isDisabled={group?.type == "hide"}
                              handleRefresh={fetchData}
                            />
                          </div>
                        </div>

                        <Droppable
                          droppableId={String(group.value)}
                          type="nested"
                          isDropDisabled
                        >
                          {(providedNest) => (
                            <ul
                              ref={providedNest.innerRef}
                              {...providedNest.droppableProps}
                              className="edit-menus-children"
                            >
                              {group.children?.map((child, cidx) => (
                                <Draggable
                                  key={child.value}
                                  draggableId={child.value}
                                  index={cidx}
                                >
                                  {(providedChild) => (
                                    <li
                                      ref={providedChild.innerRef}
                                      {...providedChild.draggableProps}
                                      className="edit-menus-child d-flex justify-content-between align-items-center"
                                    >
                                      <div className="d-flex align-items-center gap-2">
                                        <span className="edit-menus-icon edit-menus-icon--sm d-inline-flex align-items-center justify-content-center">
                                          {child.newLabel ? (
                                            <i
                                              class="fa"
                                              dangerouslySetInnerHTML={{
                                                __html: child?.icon,
                                              }}
                                            ></i>
                                          ) : (
                                            <span>{child.icon}</span>
                                          )}
                                        </span>
                                        <span className="edit-menus-child-label">
                                          {child.newLabel || child.label}
                                        </span>
                                        {child?.type === "hide" && (
                                          <span className="edit-menus-badge edit-menus-badge--muted">Hidden</span>
                                        )}
                                      </div>
                                      <div className="d-flex align-items-center gap-1">
                                        <MenuHideModal
                                          selectedItem={child}
                                          data={data}
                                          isDisabled={group?.type == "hide"}
                                          handleRefresh={fetchData}
                                        />
                                        <MenuEditsModal
                                          selectedItem={child}
                                          data={data}
                                          isDisabled={
                                            group?.type == "hide" ||
                                            child?.type == "hide"
                                          }
                                          handleRefresh={fetchData}
                                        />
                                      </div>
                                    </li>
                                  )}
                                </Draggable>
                              ))}
                              {providedNest.placeholder}
                            </ul>
                          )}
                        </Droppable>
                      </div>
                    )}
                  </Draggable>
                ))}
                {providedMain.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}

      <EditMenusStyles />
    </div>
  );
};

const EditMenusStyles = () => (
  <style>{`
    .edit-menus {
      font-family: 'Inter', sans-serif;
    }

    /* Toolbar */
    .edit-menus-search {
      max-width: 380px;
    }
    .edit-menus-search-icon {
      position: absolute;
      left: 12px;
      top: 50%;
      transform: translateY(-50%);
      color: #9ca3af;
      pointer-events: none;
    }
    .edit-menus-search-input {
      width: 100%;
      height: 38px;
      padding: 0 36px 0 36px;
      border: 1px solid #e5e5e5;
      border-radius: 8px;
      background: #fff;
      font-size: 0.875rem;
      color: #1f2937;
      transition: border-color 0.15s ease, box-shadow 0.15s ease;
    }
    .edit-menus-search-input::placeholder {
      color: #9ca3af;
    }
    .edit-menus-search-input:focus {
      outline: none;
      border-color: #4f46e5;
      box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1);
    }
    .edit-menus-search-clear {
      position: absolute;
      right: 8px;
      top: 50%;
      transform: translateY(-50%);
      width: 24px;
      height: 24px;
      border-radius: 6px;
      border: 0;
      background: transparent;
      color: #6b7280;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: background 0.15s ease, color 0.15s ease;
    }
    .edit-menus-search-clear:hover {
      background: #f3f4f6;
      color: #1f2937;
    }

    /* Group card */
    .edit-menus-card {
      background: #fff;
      border: 1px solid #ececec;
      border-radius: 12px;
      overflow: hidden;
      transition: border-color 0.15s ease, box-shadow 0.15s ease;
    }
    .edit-menus-card:hover {
      border-color: #dcdcdc;
    }
    .edit-menus-card.is-dragging {
      border-color: #4f46e5;
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
    }
    .edit-menus-card-header {
      padding: 14px 16px;
      background: #fafafa;
      border-bottom: 1px solid #ececec;
    }
    .edit-menus-drag-area {
      cursor: grab;
      min-width: 0;
    }
    .edit-menus-drag-area:active {
      cursor: grabbing;
    }
    .edit-menus-grip {
      color: #c4c4c4;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 18px;
      transition: color 0.15s ease;
    }
    .edit-menus-drag-area:hover .edit-menus-grip {
      color: #6b7280;
    }
    .edit-menus-icon {
      width: 26px;
      height: 26px;
      border-radius: 6px;
      background: #f3f4f6;
      color: #4b5563;
      font-size: 0.8125rem;
      flex-shrink: 0;
    }
    .edit-menus-icon--sm {
      width: 22px;
      height: 22px;
      font-size: 0.75rem;
    }
    .edit-menus-label {
      font-size: 0.9375rem;
      font-weight: 600;
      color: #1f2937;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .edit-menus-badge {
      display: inline-flex;
      align-items: center;
      padding: 2px 8px;
      font-size: 0.6875rem;
      font-weight: 500;
      line-height: 1.4;
      color: #1f2937;
      background: #f1f1f1;
      border-radius: 999px;
      letter-spacing: 0.01em;
    }
    .edit-menus-badge--muted {
      color: #6b7280;
      background: #f8f8f8;
      border: 1px solid #ececec;
    }

    /* Children */
    .edit-menus-children {
      list-style: none;
      margin: 0;
      padding: 6px 12px;
    }
    .edit-menus-child {
      padding: 10px 12px;
      border-radius: 8px;
      transition: background 0.15s ease;
      font-size: 0.875rem;
      color: #1f2937;
    }
    .edit-menus-child + .edit-menus-child {
      margin-top: 2px;
    }
    .edit-menus-child:hover {
      background: #fafafa;
    }
    .edit-menus-child-label {
      color: #1f2937;
    }

    /* Empty state */
    .edit-menus-empty {
      border: 1px dashed #e5e5e5;
      border-radius: 12px;
      background: #fafafa;
    }
    .edit-menus-empty-icon {
      width: 44px;
      height: 44px;
      border-radius: 50%;
      background: #fff;
      border: 1px solid #ececec;
      color: #9ca3af;
    }

    /* Loading */
    .edit-menus-loading {
      font-family: 'Inter', sans-serif;
    }
  `}</style>
);

export default EditMenus;
